import React, { useState } from "react";
import { omit } from "lodash";
import { ScanReportType, WizardStep } from "@sscs/types";
import { Dialog, QueryResult, Toast, Wizard } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { useGetWhoamiQuery } from "Services";
import { apiErrorToast } from "Utils";
import {
  useGetSharedReportLinkRecipientsQuery,
  useShareReportMutation,
} from "../../../services/sharedReports.api";
import {
  SharedReportForm,
  SharedReportInfo,
  SharedReportLinkNameForm,
  SharedReportPreviousRecipientsForm,
  SharedReportRecipientsForm,
  SharedReportRestrictionsForm,
} from "../../../types";
import {
  DEFAULT_RECIPIENTS_FORM_DATA,
  DEFAULT_RESTRICTIONS,
  getLinkNameStepInitialValues,
  getRecipientsFormInitialValues,
  getRestrictionsStepInitialValues,
  getSharedReportFormDataFromInitialSettings,
  getSharedReportLinkNameSchema,
  getSharedReportPreviousRecipientsFormSchema,
  getSharedReportRecipientsFormSchema,
  restrictionsFormSchema,
  updateRecipientsFormWithPreviousRecipients,
} from "../../../utils";
import commonStyles from "../../common.module.scss";
import WizardSteps from "../common/WizardSteps";
import {
  REGENERATE_REPORT_LINK_WIZARD_LABELS,
  RegenerateReportLinkWizardStep,
} from "./constants";

type ShareReportStepForm =
  | SharedReportLinkNameForm
  | SharedReportRestrictionsForm
  | SharedReportPreviousRecipientsForm
  | SharedReportRecipientsForm;

type RegenerateReportLinkDialogProps = {
  purl: string;
  onClose: () => void;
  isOpen: boolean;
  versionId: string;
  reportType?: ScanReportType;
  expiredReport: SharedReportInfo;
};

const RegenerateReportLinkDialogContent = ({
  purl,
  onClose,
  versionId,
  reportType,
  expiredReport,
}: Omit<RegenerateReportLinkDialogProps, "isOpen">): React.JSX.Element => {
  const [activeWizardStep, setActiveWizardStep] = useState(
    RegenerateReportLinkWizardStep.LinkNameStep,
  );
  const [settings, setSettings] = useState<SharedReportForm>(
    getSharedReportFormDataFromInitialSettings(expiredReport.settings),
  );
  const [sharedReport, setSharedReport] = useState<SharedReportInfo | null>(
    null,
  );
  const [skipRecipients, setSkipRecipients] = useState(false);
  const { data: whoami } = useGetWhoamiQuery();
  const {
    data: existingRecipients,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetSharedReportLinkRecipientsQuery({ token_key: expiredReport.token });
  const queryStatus = getQueryStatus({
    isLoading,
    isFetching,
    isError,
    error,
    data: existingRecipients,
  });

  const [shareReport, { isLoading: isSharingReport }] =
    useShareReportMutation();

  const selectedExistingRecipientEmails =
    settings.recipients !== null && existingRecipients
      ? existingRecipients
          .filter((recipient) => settings.recipients?.includes(recipient.email))
          .map((recipient) => recipient.email)
      : [];
  const initialPreviousRecipients =
    existingRecipients?.map((recipient) => ({
      email: recipient.email,
      lastOpened: recipient.last_opened,
      isSelected: selectedExistingRecipientEmails.includes(recipient.email),
    })) ?? [];

  const handleNext = (data?: ShareReportStepForm) => {
    if (data) {
      updateSharedReportSettings({ ...settings, ...data });
    }
    setActiveWizardStep((previous) => previous + 1);
  };

  const handlePrevious = (data?: ShareReportStepForm): void => {
    if (data) {
      updateSharedReportSettings({ ...settings, ...data });
    }
    setActiveWizardStep((previous) => previous - 1);
  };

  const regenerateLink = async () => {
    try {
      const recipients = settings.recipients ? [...settings.recipients] : [];

      if (settings.send_copy_to_me && whoami?.email && recipients.length > 0) {
        recipients.push(whoami.email);
      }

      const sharedReportResponse = await shareReport({
        version_id: versionId,
        report_type: reportType,
        settings: {
          ...omit(settings, ["use_case", "send_copy_to_me"]),
          recipients,
          message: recipients.length > 0 ? settings.message : "",
        },
      }).unwrap();

      setSharedReport(sharedReportResponse);
      Toast.show({
        title: "The report has been shared successfully",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not generate link");
      return;
    }
  };

  const updateSharedReportSettings = (data: SharedReportForm) => {
    setSettings((prevState) => ({
      ...prevState,
      ...data,
    }));
  };

  return (
    <Wizard
      title="Share report link"
      subtitle={<code>{purl}</code>}
      activeStep={activeWizardStep}
      steps={
        [
          {
            label:
              REGENERATE_REPORT_LINK_WIZARD_LABELS[
                RegenerateReportLinkWizardStep.LinkNameStep
              ],
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.LinkNameStep />
              </QueryResult>
            ),
            formValues: getLinkNameStepInitialValues(settings),
            schema: getSharedReportLinkNameSchema(),
            onNext: (data) => {
              if (data && data.label !== expiredReport.settings.label) {
                handleNext({
                  ...DEFAULT_RECIPIENTS_FORM_DATA,
                  ...DEFAULT_RESTRICTIONS,
                  label: data.label,
                });
              } else {
                handleNext(data);
              }
            },
            onPrevious: handlePrevious,
          },
          {
            label:
              REGENERATE_REPORT_LINK_WIZARD_LABELS[
                RegenerateReportLinkWizardStep.RestrictionsStep
              ],
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.RestrictionsStep />
              </QueryResult>
            ),
            formValues: getRestrictionsStepInitialValues(
              settings.label,
              settings,
            ),
            schema: restrictionsFormSchema,
            onNext: handleNext,
            onPrevious: handlePrevious,
          },
          {
            label:
              REGENERATE_REPORT_LINK_WIZARD_LABELS[
                RegenerateReportLinkWizardStep.PreviousRecipientsStep
              ],
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.PreviousRecipientsStep
                  initialPreviousRecipients={initialPreviousRecipients}
                />
              </QueryResult>
            ),
            formValues: {
              recipients: initialPreviousRecipients
                .filter((recipient) => recipient.isSelected)
                .map((recipient) => recipient.email),
            },
            schema: getSharedReportPreviousRecipientsFormSchema(),
            onNext: (data: SharedReportRecipientsForm) => {
              const recipientsForm = updateRecipientsFormWithPreviousRecipients(
                settings,
                (existingRecipients ?? []).map((recipient) => ({
                  email: recipient.email,
                  lastOpened: recipient.last_opened,
                  isSelected:
                    data.recipients?.includes(recipient.email) ?? false,
                })),
              );

              handleNext(recipientsForm);
            },
            onPrevious: handlePrevious,
          },
          {
            label:
              REGENERATE_REPORT_LINK_WIZARD_LABELS[
                RegenerateReportLinkWizardStep.RecipientsStep
              ],
            isOptional: skipRecipients,
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.RecipientsStep
                  onSkipRecipients={setSkipRecipients}
                />
              </QueryResult>
            ),
            formValues: getRecipientsFormInitialValues(
              settings.label,
              settings,
            ),
            schema: getSharedReportRecipientsFormSchema(false),
            formMode: "onChange",
            onNext: handleNext,
            onPrevious: handlePrevious,
          },
          {
            label:
              REGENERATE_REPORT_LINK_WIZARD_LABELS[
                RegenerateReportLinkWizardStep.ReviewStep
              ],
            nextStep: { label: "Generate link", isLoading: isSharingReport },
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.ReviewStep
                  formData={settings}
                  resentToEmailAddressesCount={0}
                  sentToEmailAddressesCount={settings.recipients?.length ?? 0}
                />
              </QueryResult>
            ),
            onNext: async () => {
              await regenerateLink();
              handleNext();
            },
            onPrevious: handlePrevious,
          },
          {
            label:
              REGENERATE_REPORT_LINK_WIZARD_LABELS[
                RegenerateReportLinkWizardStep.ShareReportStep
              ],
            nextStep: { label: "Close" },
            previousStep: { isHidden: true },
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.ShareReportStep
                  sharedReport={sharedReport}
                  sentToEmailAddressesCount={sharedReport?.recipients.count}
                />
              </QueryResult>
            ),
            onNext: onClose,
          },
        ] satisfies readonly [
          WizardStep<SharedReportLinkNameForm>,
          WizardStep<SharedReportRestrictionsForm>,
          WizardStep<SharedReportPreviousRecipientsForm>,
          WizardStep<SharedReportRecipientsForm>,
          WizardStep,
          WizardStep,
        ]
      }
      onStepChange={setActiveWizardStep}
      dialogContentClasses={commonStyles["dialog-content"]}
    />
  );
};

const RegenerateReportLinkDialog = ({
  isOpen,
  ...props
}: RegenerateReportLinkDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="md">
    <RegenerateReportLinkDialogContent {...props} />
  </Dialog.Frame>
);

export default RegenerateReportLinkDialog;
